import React, { useEffect, useState, useMemo, useCallback } from 'react'
import MainLayout from '../../../../components/MainLayout/MainLayout'
import { Col, Row, Form, Input, Select, Button, TreeSelect, Card, notification, Tag } from 'antd'
import type { SafeKey } from 'rc-tree/lib/interface'
import { colors } from '../../../../utils/colors/colors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { useTranslation } from 'react-i18next';
import {
    fetchSectorsRequest,
    fetchMainSectorsRequest,
    fetchWaterAdministrationsRequest,
    fetchAllCompaniesRequest,
    fetchCompanyTypesRequest,
    fetchCountriesRequest,
    fetchCitiesRequest,
    fetchDistrictsRequest,
    createCompanyRequest,
    fetchProductFamiliesRequest,
    clearCompanyDetail,
    fetchBasinsRequest,
    fetchSubscriptionTypesRequest,
    fetchWaterAdministrationMainSectorsRequest,
    fetchWaterAdministrationSubSectorsRequest,
} from '../../../../redux/reducers/clients/clientsSlice';
import {
    Sector,
    MainSector,
    WaterAdministration,
    CompanyType,
    Country,
    City,
    District,
    ClientFormData,
    ProductModule,
    Basin,
    SubscriptionType,
    WaterAdministrationMainSector,
    WaterAdministrationSubSector
} from '../../../../redux/reducers/clients/clients.types';
import "./AddClients.scss"
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import SearchableSelect from '../../../../components/SearchableSelect/SearchableSelect';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { SubMenuType } from 'antd/es/menu/interface';

const { TextArea } = Input;

interface SelectedModuleData {
    id: number;
    name: string;
}

interface SelectedFamilyData {
    name: string;
    modules: SelectedModuleData[];
}

const AddClients = () => {
    const { t } = useTranslation('clients');
    const [form] = Form.useForm<ClientFormData>();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const watchedProductFamily = Form.useWatch('productFamily', form);

    const {
        sectors: { data: sectorsData },
        mainSectors: { data: mainSectorsData },
        waterAdministrations: { data: waterAdministrationsData },
        subscriptionTypes: { data: subscriptionTypesData, loading: subscriptionTypesLoading },
        allCompanies: { data: allCompaniesData },
        companyTypes: { data: companyTypesData },
        countries: { data: countriesData },
        cities: { data: citiesData },
        districts: { data: districtsData },
        companyDetail: { loading: createLoading, error: createCompanyError, success: createCompanySuccess },
        productFamilies: { data: productFamiliesData },
        basins: { data: basinsData },
        waterAdministrationMainSectors: { data: waterAdministrationMainSectorsData, loading: waMainSectorsLoading },
        waterAdministrationSubSectors: { data: waterAdministrationSubSectorsData, loading: waSubSectorsLoading }
    } = useSelector((state: RootState) => state.clients);

    const [selectedMainSector, setSelectedMainSector] = useState<number | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
    const [selectedCity, setSelectedCity] = useState<number | null>(null);
    const [selectedWaterAdmin, setSelectedWaterAdmin] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [expandedKeys, setExpandedKeys] = useState<SafeKey[]>([]);
    const [selectedWaMainSector, setSelectedWaMainSector] = useState<number | null>(null);

    const isTurkey = countriesData?.find(c => c.id === selectedCountry)?.name === "Türkiye";

    const breadcrumbCrumbs = [
        { title: 'dashboard', path: '/dashboard' },
        { title: 'clients', path: '/clients' },
        { title: 'add_new_client' }
    ];

    const productFamilyTreeData = useMemo(() => {
        return (productFamiliesData ?? []).map(family => ({
            value: `family-${family.id}`,
            title: t(family.name),
            isLeaf: false,
            children: (family.productModules ?? []).map(module => ({
                value: `module-${module.id}`,
                title: t(module.name),
                isLeaf: true
            }))
        }));
    }, [productFamiliesData, t]);

    const moduleToFamilyMap = useMemo(() => {
        const map = new Map<number, { familyId: number; familyName: string }>();
        (productFamiliesData ?? []).forEach(family => {
            (family.productModules ?? []).forEach(module => {
                map.set(module.id, { familyId: family.id, familyName: family.name });
            });
        });
        return map;
    }, [productFamiliesData]);

    const allModulesMap = useMemo(() => {
        const map = new Map<number, ProductModule>();
        (productFamiliesData ?? []).forEach(family => {
            (family.productModules ?? []).forEach(module => {
                map.set(module.id, module);
            });
        });
        return map;
    }, [productFamiliesData]);

    const parsedSelectedProducts = useMemo(() => {
        if (!watchedProductFamily || moduleToFamilyMap.size === 0) {
            return { families: new Map<number, SelectedFamilyData>() };
        }
        const families = new Map<number, SelectedFamilyData>();
        watchedProductFamily.forEach(value => {
            if (String(value).startsWith('module-')) {
                const moduleId = Number(String(value).replace('module-', ''));
                const moduleInfo = allModulesMap.get(moduleId);
                const familyInfo = moduleToFamilyMap.get(moduleId);
                if (moduleInfo && familyInfo) {
                    if (!families.has(familyInfo.familyId)) {
                        families.set(familyInfo.familyId, {
                            name: familyInfo.familyName,
                            modules: []
                        });
                    }
                    families.get(familyInfo.familyId)!.modules.push({
                        id: moduleInfo.id,
                        name: moduleInfo.name
                    });
                }
            }
        });
        return { families };
    }, [watchedProductFamily, moduleToFamilyMap, allModulesMap]);

    const baseDataViewerTreeData = useMemo(() => {
        if (!allCompaniesData || parsedSelectedProducts.families.size === 0) return [];
        return allCompaniesData.map(company => {
            const familyChildren = Array.from(parsedSelectedProducts.families.entries()).map(([familyId, familyData]) => {
                const moduleChildren = familyData.modules.map(module => ({
                    value: `module-${module.id}-company-${company.id}`,
                    title: t(module.name),
                    isLeaf: true,
                }));
                return {
                    value: `family-${familyId}-company-${company.id}`,
                    title: t(familyData.name),
                    isLeaf: false,
                    children: moduleChildren,
                };
            });
            return {
                value: `company-${company.id}`,
                title: company.name,
                isLeaf: familyChildren.length === 0,
                children: familyChildren,
            };
        });
    }, [allCompaniesData, parsedSelectedProducts, t]);

    const companyMap = useMemo(() => {
        if (!allCompaniesData) return new Map();
        return new Map(allCompaniesData.map(company => [company.id, company.name]));
    }, [allCompaniesData]);

    const customTagRender = (props: any) => {
        const { label, value, closable, onClose } = props;
        const parts = String(value).split('-company-');
        if (String(value).startsWith('module-') && parts.length === 2) {
            const companyId = parts[1];
            const companyName = companyMap.get(companyId);
            if (companyName) {
                const displayLabel = `${companyName} > ${label}`;
                return (
                    <Tag closable={closable} onClose={onClose} style={{ margin: '2px' }}>
                        {displayLabel}
                    </Tag>
                );
            }
        }

        return (
            <Tag closable={closable} onClose={onClose} style={{ margin: '2px' }}>
                {label}
            </Tag>
        );
    };

    const filterTreeData = useCallback((data: any[], searchValue: string): any[] => {
        if (!searchValue) return data;
        const filterNode = (node: any): any | null => {
            const title = node.title?.toString().toLowerCase() || '';
            const matches = title.includes(searchValue.toLowerCase());
            let filteredChildren: any[] = [];
            if (node.children) {
                filteredChildren = node.children.map(filterNode).filter(Boolean);
            }
            if (matches || filteredChildren.length > 0) {
                return { ...node, children: filteredChildren };
            }
            return null;
        };
        return data.map(filterNode).filter(Boolean);
    }, []);

    const filteredDataViewerTreeData = useMemo(() => {
        return filterTreeData(baseDataViewerTreeData, searchValue);
    }, [baseDataViewerTreeData, searchValue, filterTreeData]);


    const handleFormChange = (changedValues: Record<string, any>) => {
        if (changedValues.countryId) {
            setSelectedCountry(changedValues.countryId);
            form.setFieldsValue({ cityId: undefined, districtId: undefined });
            setSelectedCity(null);
            dispatch(fetchCitiesRequest(changedValues.countryId));
        }
        if (changedValues.cityId) {
            setSelectedCity(changedValues.cityId);
            form.setFieldsValue({ districtId: undefined });
            if (isTurkey) {
                dispatch(fetchDistrictsRequest(changedValues.cityId));
            }
        }
        if (changedValues.mainSectorId !== undefined) {
            setSelectedMainSector(changedValues.mainSectorId);
            form.setFieldsValue({ sectorId: undefined });
        }
        if (changedValues.waterAdministrationId !== undefined) {
            setSelectedWaterAdmin(changedValues.waterAdministrationId);
            form.setFieldsValue({ subscriptionTypeId: undefined });
            dispatch(fetchSubscriptionTypesRequest(changedValues.waterAdministrationId));

            form.setFieldsValue({ waterAdministrationMainSectorId: undefined, waterAdministrationSubSectorId: undefined });
            setSelectedWaMainSector(null);
            dispatch(fetchWaterAdministrationMainSectorsRequest(changedValues.waterAdministrationId));
        }

        if (changedValues.waterAdministrationMainSectorId !== undefined) {
            setSelectedWaMainSector(changedValues.waterAdministrationMainSectorId);

            // Alt sektörleri getir (Eski seçimi temizle)
            form.setFieldsValue({ waterAdministrationSubSectorId: undefined });
            dispatch(fetchWaterAdministrationSubSectorsRequest(changedValues.waterAdministrationMainSectorId));
        }
    };

    const processDataViewerCompanies = (
        selectedValues: string[],
        moduleToFamilyMap: Map<number, { familyId: number; familyName: string }>
    ) => {
        const companiesMap = new Map<string, { families: Set<number>; modules: Set<number> }>();
        if (!selectedValues) return [];
        selectedValues.forEach(value => {
            const parts = value.split('-company-');
            if (parts.length !== 2) return;
            const companyId = parts[1];
            const typeAndId = parts[0];
            if (!companiesMap.has(companyId)) {
                companiesMap.set(companyId, { families: new Set(), modules: new Set() });
            }
            const companyData = companiesMap.get(companyId)!;
            if (typeAndId.startsWith('module-')) {
                const moduleId = parseInt(typeAndId.replace('module-', ''), 10);
                if (isNaN(moduleId)) return;
                companyData.modules.add(moduleId);
                const familyInfo = moduleToFamilyMap.get(moduleId);
                if (familyInfo) {
                    companyData.families.add(familyInfo.familyId);
                }
            } else if (typeAndId.startsWith('family-')) {
                const familyId = parseInt(typeAndId.replace('family-', ''), 10);
                if (!isNaN(familyId)) {
                    companyData.families.add(familyId);
                }
            }
        });
        return Array.from(companiesMap.entries()).map(([companyId, data]) => ({
            companyId,
            productFamilies: Array.from(data.families),
            productModules: Array.from(data.modules),
        }));
    };

    const onFinish = (values: ClientFormData) => {
        const { productFamily, dataViewerCompanies: viewerCompaniesValues, ...restOfValues } = values;

        const separateProductData = (
            selectedValues: string[],
            moduleToFamilyMap: Map<number, { familyId: number; familyName: string }>
        ) => {
            const familyIdSet = new Set<number>();
            const moduleIds: number[] = [];
            if (!selectedValues) return { productFamilyIds: [], productModuleIds: [] };
            selectedValues.forEach(value => {
                if (String(value).startsWith('module-')) {
                    const moduleId = Number(String(value).replace('module-', ''));
                    if (!isNaN(moduleId)) {
                        moduleIds.push(moduleId);
                        const familyInfo = moduleToFamilyMap.get(moduleId);
                        if (familyInfo) {
                            familyIdSet.add(familyInfo.familyId);
                        }
                    }
                } else if (String(value).startsWith('family-')) {
                    const familyId = Number(String(value).replace('family-', ''));
                    if (!isNaN(familyId)) {
                        familyIdSet.add(familyId);
                    }
                }
            });
            return { productFamilyIds: Array.from(familyIdSet), productModuleIds: moduleIds };
        };

        const { productFamilyIds, productModuleIds } = separateProductData(productFamily, moduleToFamilyMap);
        const dataViewerCompanies = processDataViewerCompanies(viewerCompaniesValues, moduleToFamilyMap);
        const backendData = {
            ...values,
            totalArea: Number(values.totalArea),
            gardenTotalArea: Number(values.gardenTotalArea),
            activeWellCount: Number(values.activeWellCount),
            passiveWellCount: Number(values.passiveWellCount),
            latitude: Number(values.latitude),
            longitude: Number(values.longitude),
            receivingEnvironmentBasinId: values.receivingEnvironmentBasinId,
            consumptionBasinId: values.consumptionBasinId,
            deepDischargeStatus: values.deepDischargeStatus,
            dischargeToReceivingEnvironmentStatus: values.dischargeToReceivingEnvironmentStatus,
            municipalitySewageDischargeStatus: values.municipalitySewageDischargeStatus,
            osbNetworkDischargeStatus: values.osbNetworkDischargeStatus,
            waterAdministrationMainSectorId: values.waterAdministrationMainSectorId,
            waterAdministrationSubSectorId: values.waterAdministrationSubSectorId,
            productFamilyIds,
            productModuleIds,
            dataViewerCompanies,
            osbAfillitonStatus: values.osbAfillitonStatus || false,
            isDemo: false,
        };
        delete (backendData as any).productFamily;

        dispatch(createCompanyRequest(backendData));
    };

    useEffect(() => {
        dispatch(fetchMainSectorsRequest());
        dispatch(fetchSectorsRequest());
        dispatch(fetchWaterAdministrationsRequest());
        dispatch(fetchAllCompaniesRequest());
        dispatch(fetchCompanyTypesRequest());
        dispatch(fetchCountriesRequest());
        dispatch(fetchProductFamiliesRequest());
        dispatch(fetchBasinsRequest());
    }, [dispatch]);

    useEffect(() => {
        if (createCompanyError) {
            notification.error({ message: t(createCompanyError) });
            dispatch(clearCompanyDetail());
        }
    }, [createCompanyError, dispatch, t]);

    useEffect(() => {
        if (createCompanySuccess) {
            notification.success({ message: t("client_add_success") });
            form.resetFields();
            dispatch(clearCompanyDetail());
            setTimeout(() => {
                navigate('/clients');
            }, 1500);
        }
    }, [createCompanySuccess, dispatch, form, navigate, t]);

    const companyTypeOptions = companyTypesData?.map((ct: CompanyType) => ({
        value: ct.id,
        label: t(ct.name)
    }));

    const osbAffiliationOptions = [
        { value: true, label: t("affiliated") },
        { value: false, label: t("not_affiliated") }
    ];

    const yesNoOptions = [
        { value: true, label: t("yes") || "Evet" },
        { value: false, label: t("no") || "Hayır" }
    ];

    const basinOptions = (basinsData || []).map((basin: Basin) => ({
        value: basin.id,
        label: basin.name
    }));

    const waterAdministrationOptions = waterAdministrationsData?.map((admin: WaterAdministration) => ({
        value: admin.id,
        label: admin.name
    }));

    const subscriptionTypeOptions = subscriptionTypesData?.map((type: SubscriptionType) => ({
        value: type.id,
        label: type.name
    }));

    const waMainSectorOptions = (waterAdministrationMainSectorsData || []).map((sector: WaterAdministrationMainSector) => ({
        value: sector.id,
        label: `${sector.code} - ${sector.name}`
    }));

    const waSubSectorOptions = (waterAdministrationSubSectorsData || []).map((sector: WaterAdministrationSubSector) => ({
        value: sector.id,
        label: sector.subSectorCode
            ? `${sector.subSectorCode} - ${sector.name}`
            : sector.name
    }));

    const countryOptions = countriesData?.map((country: Country) => ({
        value: country.id,
        label: country.name
    }));

    const cityOptions = citiesData?.map((city: City) => ({
        value: city.id,
        label: city.name
    }));

    const districtOptions = districtsData?.map((district: District) => ({
        value: district.id,
        label: district.name
    }));

    const mainSectorOptions = mainSectorsData?.map((sector: MainSector) => ({
        value: sector.id,
        label: `${sector.name} - ${sector.naceCode}`
    }));

    const sectorOptions = sectorsData
        ?.filter((sector: Sector) => sector.mainSectorId === selectedMainSector)
        .map((sector: Sector) => ({
            value: sector.id,
            label: `${sector.name} - ${sector.naceCode}`
        }));

    return (
        <MainLayout>
            <PageHeader crumbs={breadcrumbCrumbs} />
            <Row style={{ paddingBottom: 24 }}>
                <Col className='base-title-typography' style={{ display: "flex", justifyContent: "center", paddingBottom: 24 }} xs={24}>
                    {t("add_new_client")}
                </Col>
                <Col xs={24}>
                    <Card>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            onValuesChange={handleFormChange}
                            autoComplete="off"
                        >
                            <Row gutter={24}>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("company_name")} name="name" rules={[{ required: true, message: t("company_name_required") }]}>
                                        <Input placeholder={t("select_company_name")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("email")} name="email" rules={[{ required: true, message: t("email_required") }, { type: 'email', message: t("valid_email_required") }]}>
                                        <Input placeholder={t("select_email")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("phone_number")} name="phoneNumber" rules={[{ required: true, message: t("phone_required") }, { pattern: /^(\+90|0)?[5]\d{2}[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}$/, message: t("valid_phone_required") }]}>
                                        <Input placeholder={t("select_phone")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("product_family")} name="productFamily" rules={[{ required: true, message: t("product_family_required") }]}>
                                        <TreeSelect className='blueit-tree-select' style={{ width: '100%' }} placeholder={t("select_product_family")} showSearch={false} allowClear multiple treeDefaultExpandAll treeData={productFamilyTreeData} treeCheckable showCheckedStrategy={TreeSelect.SHOW_ALL} virtual={true} listHeight={250} maxTagCount="responsive" getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("customer_type")} name="companyTypeId" rules={[{ required: true, message: t("customer_type_required") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_customer_type")}
                                            options={companyTypeOptions || []}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("data_viewer_companies")} name="dataViewerCompanies">
                                        <TreeSelect
                                            className='blueit-tree-select'
                                            showSearch
                                            style={{ width: '100%' }}
                                            placeholder={t("select_data_viewer_companies")}
                                            allowClear
                                            multiple
                                            treeData={filteredDataViewerTreeData}
                                            treeCheckable
                                            showCheckedStrategy={TreeSelect.SHOW_CHILD}
                                            virtual={true}
                                            listHeight={300}
                                            treeExpandedKeys={expandedKeys}
                                            onTreeExpand={(keys: SafeKey[]) => setExpandedKeys(keys)}
                                            maxTagCount="responsive"
                                            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                                            filterTreeNode={false}
                                            onSearch={setSearchValue}
                                            searchValue={searchValue}
                                            notFoundContent={watchedProductFamily && watchedProductFamily.length > 0 ? (searchValue && filteredDataViewerTreeData.length === 0 ? t("no_data_found") : null) : t("select_product_family_first")}
                                            tagRender={customTagRender}
                                            disabled={!watchedProductFamily || watchedProductFamily.length === 0}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={8}>
                                    <Form.Item className='base-table-text' label={t("osb_affiliation_status")} name="osbAfillitonStatus">
                                        <Select
                                            placeholder={t("select_osb_status")}
                                            options={osbAffiliationOptions}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={8}>
                                    <Form.Item className='base-table-text' label={t("water_administration")} name="waterAdministrationId" rules={[{ required: true, message: t("water_administration_required") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_water_administration")}
                                            options={waterAdministrationOptions || []}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={8}>
                                    <Form.Item className='base-table-text' label={t("subscription_type")} name="subscriptionTypeId">
                                        <SearchableSelect
                                            placeholder={t("select_subscription_type")}
                                            options={subscriptionTypeOptions || []}
                                            disabled={!selectedWaterAdmin}
                                            loading={subscriptionTypesLoading}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item
                                        className='base-table-text'
                                        label={t("water_admin_main_sector") || "İdare Ana Sektör (KÖP)"}
                                        name="waterAdministrationMainSectorId"
                                    >
                                        <SearchableSelect
                                            placeholder={t("select") || "Seçiniz"}
                                            options={waMainSectorOptions}
                                            disabled={!selectedWaterAdmin}
                                            loading={waMainSectorsLoading}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item
                                        className='base-table-text'
                                        label={t("water_admin_sub_sector") || "İdare Alt Sektör (KÖP)"}
                                        name="waterAdministrationSubSectorId"
                                    >
                                        <SearchableSelect
                                            placeholder={t("select") || "Seçiniz"}
                                            options={waSubSectorOptions}
                                            disabled={!selectedWaMainSector}
                                            loading={waSubSectorsLoading}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={8}>
                                    <Form.Item className='base-table-text' label={t("company_country")} name="countryId" rules={[{ required: true, message: t("country_required") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_country")}
                                            options={countryOptions || []}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={8}>
                                    <Form.Item className='base-table-text' label={t("company_city")} name="cityId" rules={[{ required: true, message: t("city_required") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_city")}
                                            disabled={!selectedCountry}
                                            options={cityOptions || []}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={8}>
                                    <Form.Item className='base-table-text' label={t("district")} name="districtId" rules={isTurkey ? [{ required: true, message: t("district_required") }] : []}>
                                        <SearchableSelect
                                            placeholder={t("select_district")}
                                            disabled={!selectedCity || !isTurkey}
                                            options={districtOptions || []}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item className='base-table-text' label={t("company_address")} name="address" rules={[{ required: true, message: t("address_required") }]}>
                                        <TextArea rows={2} placeholder={t("enter_address")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("latitude")} name="latitude">
                                        <Input type="number" placeholder={t("enter_latitude")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("longitude")} name="longitude">
                                        <Input type="number" placeholder={t("enter_longitude")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("main_sector")} name="mainSectorId" rules={[{ required: true, message: t("main_sector_required") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_main_sector")}
                                            options={mainSectorOptions || []}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("secondary_sector")} name="sectorId" rules={[{ required: true, message: t("secondary_sector_required") }]}>
                                        <SearchableSelect
                                            allowClear
                                            placeholder={t("select_secondary_sector")}
                                            options={sectorOptions || []}
                                            disabled={!selectedMainSector}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("active_well_count")} name="activeWellCount" rules={[{ required: true, message: t("active_well_count_required") }]}>
                                        <Input type="number" placeholder={t("enter_active_well_count")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("passive_well_count")} name="passiveWellCount" rules={[{ required: true, message: t("passive_well_count_required") }]}>
                                        <Input type="number" placeholder={t("enter_passive_well_count")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("total_area")} name="totalArea" rules={[{ required: true, message: t("total_area_required") }]}>
                                        <Input type="number" placeholder={t("enter_total_area")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("garden_total_area")} name="gardenTotalArea" rules={[{ required: true, message: t("garden_area_required") }]}>
                                        <Input type="number" placeholder={t("enter_garden_area")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("receiving_environment_basin") || "Alıcı Ortam Havzası"} name="receivingEnvironmentBasinId" rules={[{ required: true, message: t("required_field") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_basin") || "Havza Seçiniz"}
                                            options={basinOptions}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("consumption_basin") || "Tüketim Havzası"} name="consumptionBasinId" rules={[{ required: true, message: t("required_field") }]}>
                                        <SearchableSelect
                                            placeholder={t("select_basin") || "Havza Seçiniz"}
                                            options={basinOptions}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("deep_discharge_status") || "Derin deşarj durumu?"} name="deepDischargeStatus" rules={[{ required: true, message: t("required_field") }]}>
                                        <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("discharge_to_receiving_environment_status") || "Alıcı Ortama Deşarj Durumu?"} name="dischargeToReceivingEnvironmentStatus" rules={[{ required: true, message: t("required_field") }]}>
                                        <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("municipality_sewage_discharge_status") || "Belediye Kanalizasyon Şebekesi Deşarj durumu?"} name="municipalitySewageDischargeStatus" rules={[{ required: true, message: t("required_field") }]}>
                                        <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12}>
                                    <Form.Item className='base-table-text' label={t("osb_network_discharge_status") || "OSB Şebekesi deşarj var mı?"} name="osbNetworkDischargeStatus" rules={[{ required: true, message: t("required_field") }]}>
                                        <Select placeholder={t("select") || "Seçiniz"} options={yesNoOptions} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify={"end"}>
                                <Col>
                                    <Button type="primary" style={{ background: colors.blueitLightBlue }} htmlType="submit" size="middle" loading={createLoading}>
                                        {t("save")}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </MainLayout>
    )
}

export default AddClients;